import { TextContent } from '@amzn/awsui-components-react-v3';
import { HelpPanelConfig } from './helpPanelTypes';

export const EXPORT_INVOICE_PANEL_CONTENT: HelpPanelConfig = {
    route: '/activities/clipper-export',
    title: 'Export Invoice',
    content: (
        <TextContent>
            <p>
                Access and download billing information for your activities.
                Exporting invoicing data allows for precise billing and
                adaptable billing scenarios. This tool is commonly used to
                manually upload invoices to Clipper, handle billing in
                unsupported countries, and review billing data before month-end.
            </p>
            <p>
                Invoice data include a CSV file of activities that ended in the
                current month and the past 2 months. To get started, select a
                target geography region and submit the export.{' '}
            </p>
        </TextContent>
    ),
    links: null,
};

export const ACTIVITY_HELP_PANEL_CONTENT = {
    route: '/activities',
    title: 'Activities',
    content: (
        <TextContent>
            <p>
                Grimsby activities are scheduled instructor-led training
                deliveries that training administrators can create and manage in
                the Schedule Management console. You can use the activities page
                to import, schedule, monitor, and maintain training deliveries
                for supported organizations, even across multiple departments or
                locations.
            </p>
            <p>
                Begin training scheduling by creating activities; use single
                entry or bulk Excel upload (up to 100 activities) for public
                classes outside Grimsby and SFDC. You can create unlimited
                activities based on your customer's needs. All activities,
                regardless of creation method, are centrally managed and visible
                to administrators with the appropriate permissions.
            </p>
            <p>
                Note: For activities flowing through Salesforce, please use the
                SFDC queue rather than bulk import to ensure proper system
                synchronization.
            </p>
        </TextContent>
    ),
    links: [
        {
            text: 'User guide',
            href: 'https://tiny.amazon.com/vwctffsu/wamazbinprevAWS_ProdILTFlas',
        },
    ],
};

export const ACTIVITY_GROUP_HELP_PANEL_CONTENT = {
    route: '/activities/group',
    title: 'Activity Groups',
    content: (
        <TextContent>
            <p>
                Activity Groups are a collection of activities (i.e. classes)
                used to deliver a course offering. This is primarily used to
                schedule and track AWS Cloud Institute (ACI) training
                deliveries, where each Activity Group represents a course
                offering for a specific quarter.
            </p>
            <p>
                To get started, first create the activity group details with the
                appropriate program type and corresponding course. Each course
                is mapped to a Learning Plan in T&C’s Learning CMS. After
                successful creation, add the delivery details with the desired
                Live Learning options.
            </p>
        </TextContent>
    ),
    links: [
        {
            text: 'User guide',
            href: 'https://w.amazon.com/bin/view/AWS_Training_and_Certification/Product_Development/ILT/Flask/Programs/ACI',
        },
        {
            text: 'Report an Issue',
            href: 'https://t.corp.amazon.com/create/templates/8c20c128-8cc5-4cb9-bb25-29da28724e79',
        },
    ],
};

export const INSTRUCTOR_HELP_PANEL_CONTENT = {
    route: '/activities/instructors',
    title: 'Instructor Schedule',
    content: (
        <TextContent>
            <p>
                A comprehensive calendar view of all instructor-related
                availability. Instructor schedule is essential for efficiently
                managing instructor resources and planning training schedules
                for instructors. You can use this view to track both podium and
                non-podium time commitments for all instructors in a single,
                consolidated display. The schedule displays everything that
                appears on an instructor's calendar, providing complete
                visibility into their time allocation. You can apply various
                filters to focus on specific instructor-related criteria and
                manage scheduling more effectively. When using filters in this
                view, they are specifically tied to instructor attributes rather
                than training activity details.
            </p>
        </TextContent>
    ),
    links: null,
};

export const SALESFORCE_QUEUE_HELP_PANEL_CONTENT = {
    route: '/activities/sfdc-queue',
    title: 'Salesforce Queue',
    content: (
        <TextContent>
            <p>
                The Salesforce (SFDC) queue allows for seamless creation of
                training activities through a structured workflow between SFDC
                and Grimsby, ensuring proper synchronization of training data
                across both systems. Approved SFDC scheduling requests initiate
                the transmission of new activity data to Grimsby. Each product
                line from the Salesforce scheduling request creates a separate
                line item in the SFDC queue, where confirmation is required
                before appearing in the activities console as an activity.
            </p>
        </TextContent>
    ),
    links: null,
};

export const CLASSROOM_PANEL_CONTENT: HelpPanelConfig = {
    route: '/activities',
    title: 'Classrooms details',
    content: (
        <TextContent>
            <p>
                Classrooms (a.k.a Builder Labs for classes) allows instructors
                to manage all aspects of their classroom experience in one
                centralized location. This includes managing AWS labs
                environments for their learners, tracking attendance, sharing
                video conferencing details and providing course materials to
                learners.
            </p>
            <p>
                Create a Classroom here for the instructor to view and access
                their class in the “Upcoming & Active Class” Classroom dashboard
                on the day of class. Before creating a classroom, ensure your
                activity meets the required conditions:
            </p>
            <ul>
                <li>Active status</li>
                <li>Class size greater than 0</li>
                <li>
                    Supported program type: Commercial, Partner, Skills Centers,
                    World Wide Field Enablement, Partner Reseller, or AWS
                    Authorized Instructor (AAI)
                </li>
                <li>Valid vILT link and meeting ID for virtual activities</li>
                <li>At least one instructor assigned</li>
            </ul>
            <h3>Security Guidelines</h3>
            <p>When using WebEx as your VILT type with CustomerLMS:</p>
            <ul>
                <li>Only secure, authenticated EVCSP links are allowed</li>
                <li>
                    Direct WebEx URLs cannot be used as they pose a security
                    risk
                </li>
                <li>
                    EVCSP links ensure proper authentication and attendance
                    tracking
                </li>
            </ul>
            <h3>Using EVCSP Links</h3>
            <p>EVCSP links have a format like: https://evcsp.webex.com/[...]</p>
            <p>
                <strong>Note:</strong> If you encounter an error in the
                classroom creation workflow, verify the above conditions and
                attempt the action a second time. If this does not work, report
                an issue.
            </p>
        </TextContent>
    ),
    links: [
        {
            text: 'Report an Issue',
            href: 'https://t.corp.amazon.com/create/templates/e7823b9c-1683-42e7-bca9-10b60e28eeee',
        },
    ],
};
